import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// KeywordIP palette
const OLIVE = '#3d4128';
const SAGE = '#b9c096';
const CREAM = '#fbf8f3';
const INK = '#1f2937';
const MUTE = '#64748b';

function fmt(n) {
  if (n === null || n === undefined) return '\u2014';
  return Number(n).toLocaleString();
}

// Signed change, plain and honest. Up = green, down = red. Never hidden.
function Delta({ change }) {
  if (change === null || change === undefined || change === 0) {
    return <span style={{ fontSize: 12, color: MUTE, fontWeight: 600 }}>no change vs last month</span>;
  }
  const up = change > 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: up ? '#166534' : '#991b1b' }}>
      {up ? '\u25b2 +' : '\u25bc '}{Math.abs(change).toLocaleString()} vs last month
    </span>
  );
}

function stageBy(stages, label) {
  return (stages || []).find(s => s.label === label) || { value: 0, change: 0, note: '' };
}

// One of the three parallel capture channels. Equal weight, side by side, so the
// reader sees them as siblings — not as a descending funnel where one feeds the next.
function ChannelCard({ stage }) {
  return (
    <div style={{ flex: '1 1 180px', minWidth: 180, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: MUTE, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{stage.label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: INK, lineHeight: 1, marginBottom: 6 }}>{fmt(stage.value)}</div>
      <Delta change={stage.change} />
      {stage.note && <div style={{ fontSize: 11, color: MUTE, marginTop: 6, lineHeight: 1.4 }}>{stage.note}</div>}
    </div>
  );
}

export default function FunnelReport({ websiteId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getFunnelReport(websiteId);
      setReport(data);
    } catch (e) {
      setError(e.response && e.response.data && e.response.data.error ? e.response.data.error : e.message);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  if (!websiteId) {
    return <div style={{ padding: 40, color: MUTE, fontSize: 14 }}>Select a website to see its results.</div>;
  }
  if (loading && !report) {
    return <div style={{ padding: 40, color: MUTE, fontSize: 14 }}>Building the combined results report{'\u2026'}</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: '#991b1b', fontSize: 14, marginBottom: 12 }}>Could not build the report: {error}</div>
        <button onClick={load} style={{ padding: '8px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (!report) return null;

  const stages = report.stages || [];
  const visitors = stageBy(stages, 'Website visitors');
  const fromGoogle = stageBy(stages, 'From Google search');
  const conversations = stageBy(stages, 'AI conversations');
  const leads = stageBy(stages, 'Leads captured');
  const booked = stageBy(stages, 'Booked consultations');

  return (
    <div>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #funnel-report, #funnel-report * { visibility: visible; }
          #funnel-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
          .no-print { display: none !important; }
          nav, header { display: none !important; }
        }
      `}</style>

      <div id="funnel-report" style={{ padding: '20px 24px', maxWidth: 940 }}>
        {/* Header — KeywordIP branding */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, borderBottom: '2px solid ' + OLIVE, paddingBottom: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: OLIVE, textTransform: 'uppercase', letterSpacing: '1px' }}>KeywordIP</div>
            <h1 style={{ margin: '4px 0 2px', fontSize: 24, color: INK, fontWeight: 800 }}>Client Results</h1>
            <div style={{ fontSize: 13, color: MUTE }}>{report.domain} {'\u00b7'} {report.report_month}</div>
          </div>
          <button
            className="no-print"
            onClick={() => window.print()}
            style={{ padding: '9px 18px', background: OLIVE, color: CREAM, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Download PDF
          </button>
        </div>

        {/* Verdict — straight from real booking deltas, never softened */}
        <div style={{ background: CREAM, border: '1px solid ' + SAGE, borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: OLIVE, lineHeight: 1.5 }}>{report.verdict}</div>
          <div style={{ fontSize: 12.5, color: MUTE, marginTop: 4 }}>Compared with {report.comparison_month}.</div>
        </div>

        {/* TOP ANCHOR — the visitor pool */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px', marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: MUTE, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Website visitors</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 42, fontWeight: 800, color: INK, lineHeight: 1 }}>{fmt(visitors.value)}</div>
            <Delta change={visitors.change} />
          </div>
          <div style={{ fontSize: 12, color: MUTE, marginTop: 6 }}>{visitors.note}</div>
        </div>

        {/* Connector label — makes the parallel nature explicit */}
        <div style={{ textAlign: 'center', fontSize: 12.5, color: MUTE, padding: '10px 0 6px' }}>
          How that traffic converted {'\u2014'} three separate channels, not steps in a chain
        </div>

        {/* THREE PARALLEL CHANNELS — equal weight, side by side */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <ChannelCard stage={fromGoogle} />
          <ChannelCard stage={conversations} />
          <ChannelCard stage={leads} />
        </div>

        <div style={{ textAlign: 'center', fontSize: 20, color: SAGE, lineHeight: 1, marginBottom: 6 }}>{'\u2193'}</div>

        {/* BOTTOM ANCHOR — the outcome that matters, emphasized */}
        <div style={{ background: OLIVE, borderRadius: 12, padding: '22px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: SAGE, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Booked consultations</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 46, fontWeight: 800, color: CREAM, lineHeight: 1 }}>{fmt(booked.value)}</div>
            <span style={{ fontSize: 13, fontWeight: 700, color: booked.change > 0 ? '#bbf7d0' : (booked.change < 0 ? '#fecaca' : SAGE) }}>
              {booked.change === 0 ? 'no change vs last month'
                : (booked.change > 0 ? '\u25b2 +' : '\u25bc ') + Math.abs(booked.change) + ' vs last month'}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: SAGE, marginTop: 6 }}>{booked.note} {'\u2014'} the bookings that came from all of the above</div>
        </div>

        {/* Honest footer */}
        <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.6 }}>
          Covering {report.report_month} versus {report.comparison_month}. The three middle figures are separate capture
          channels, not sequential steps {'\u2014'} a visitor can become a lead through a web form without using the AI
          receptionist, so these numbers are not meant to add up or narrow in sequence. Google search shows organic
          clicks with branded searches excluded. Conversations count AI receptionist chats with real back-and-forth.
          Leads exclude deleted and spam submissions. Booked consultations are confirmed appointments only, excluding
          cancellations.
        </div>
        <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 10 }}>Prepared by KeywordIP {'\u00b7'} {new Date(report.generated_at + 'Z').toLocaleDateString()}</div>
      </div>
    </div>
  );
}
